/**
 * Two Column Layout Component
 * 
 * Responsive two-column grid layout with flexible configuration.
 * Used for purchase flows, review pages, and payment forms.
 * 
 * **Features:**
 * - Multiple column ratio variants (50/50, 60/40, 40/60)
 * - Configurable gap sizes
 * - Sticky right column option
 * - Mobile-first responsive design
 * - Configurable mobile ordering
 * 
 * **Usage:**
 * ```tsx
 * // Simple two-column layout (natural stacking on mobile)
 * <TwoColumnLayout
 *   left={<BayImage />}
 *   right={<BookingDetails />}
 * />
 * 
 * // Payment flow (right-first on mobile, larger gap)
 * <TwoColumnLayout
 *   gap="xl"
 *   mobileOrder="right-first"
 *   left={<PriceSummaryCard />}
 *   right={<PaymentForm />}
 * />
 * 
 * // Subscription review (sticky summary)
 * <TwoColumnLayout
 *   ratio="60-40"
 *   stickyRight={true}
 *   left={<SubscriptionDetails />}
 *   right={<PriceSummaryCard />}
 * />
 * ```
 * 
 * @module components/layouts
 */

import type { ReactNode } from 'react';

/**
 * Props for TwoColumnLayout component
 */
export interface TwoColumnLayoutProps {
  /**
   * Left column content
   * 
   * Typically: forms, booking details, subscription cards
   */
  left: ReactNode;
  
  /**
   * Right column content
   * 
   * Typically: price summaries, payment forms, action buttons
   */
  right: ReactNode;
  
  /**
   * Column ratio variant
   * 
   * - `50-50`: Equal columns (default, matches booking flow)
   * - `60-40`: Left wider (purchase flows)
   * - `40-60`: Right wider (rare)
   * 
   * @default '50-50'
   */
  ratio?: '50-50' | '60-40' | '40-60';
  
  /**
   * Gap size between columns
   * 
   * - `sm`: gap-4 (1rem / 16px)
   * - `md`: gap-8 (2rem / 32px) - default
   * - `lg`: gap-12 (3rem / 48px)
   * - `xl`: gap-8 lg:gap-24 (2rem mobile / 6rem desktop) - for payment flows
   * 
   * @default 'md'
   */
  gap?: 'sm' | 'md' | 'lg' | 'xl';
  
  /**
   * Make right column sticky on scroll (desktop only)
   * 
   * Useful for price summaries that should stay visible.
   * 
   * @default false
   */
  stickyRight?: boolean;
  
  /**
   * Column ordering on mobile
   * 
   * - `natural`: No explicit ordering, columns stack in natural order (default)
   * - `right-first`: Right column appears first on mobile
   * - `left-first`: Left column appears first on mobile
   * 
   * @default 'natural'
   */
  mobileOrder?: 'natural' | 'left-first' | 'right-first';
  
  /**
   * Add top margin
   * 
   * @default true
   */
  topMargin?: boolean;
  
  /**
   * Custom class for grid container
   */
  className?: string;
  
  /**
   * Custom class for left column
   */
  leftClassName?: string;
  
  /**
   * Custom class for right column
   */
  rightClassName?: string;
}

/**
 * Grid template column mappings
 */
const ratioClasses = {
  '50-50': 'lg:grid-cols-2',
  '60-40': 'lg:grid-cols-[1fr,400px]',
  '40-60': 'lg:grid-cols-[400px,1fr]',
} as const;

/**
 * Gap size mappings
 */
const gapClasses = {
  sm: 'gap-4',
  md: 'gap-8',
  lg: 'gap-12',
  xl: 'gap-8 lg:gap-24', // Payment flow gap: 2rem mobile, 6rem desktop
} as const;

/**
 * Two Column Layout Component
 * 
 * Creates a responsive two-column layout with configurable ratios and behavior.
 * On mobile (< lg breakpoint), columns stack vertically with configurable order.
 * 
 * Matches PaymentDetailsStep pattern for consistent responsive behavior.
 */
export function TwoColumnLayout({
  left,
  right,
  ratio = '50-50',
  gap = 'md',
  stickyRight = false,
  mobileOrder = 'natural',
  topMargin = true,
  className = '',
  leftClassName = '',
  rightClassName = '',
}: TwoColumnLayoutProps) {
  // Calculate order classes based on mobile preference
  const leftOrderClasses = mobileOrder === 'right-first' 
    ? 'order-2 lg:order-1'  // Left shows second on mobile (below payment)
    : mobileOrder === 'left-first'
    ? 'order-1 lg:order-1'  // Left shows first on mobile
    : '';                   // Natural order (no explicit ordering)
  
  const rightOrderClasses = mobileOrder === 'right-first'
    ? 'order-1 lg:order-2'  // Right shows first on mobile (payment/booking details on top)
    : mobileOrder === 'left-first'
    ? 'order-2 lg:order-2'  // Right shows second on mobile
    : '';                   // Natural order (no explicit ordering)
  
  return (
    <div 
      className={`
        grid grid-cols-1 ${ratioClasses[ratio]} ${gapClasses[gap]}
        ${topMargin ? 'mt-10' : ''}
        ${className}
      `}
    >
      {/* Left Column */}
      <div className={`space-y-6 ${leftOrderClasses} ${leftClassName}`}>
        {left}
      </div>
      
      {/* Right Column */}
      <div className={`space-y-8 ${rightOrderClasses} ${rightClassName}`}>
        {stickyRight ? (
          <div className="lg:sticky lg:top-8">
            {right}
          </div>
        ) : (
          right
        )}
      </div>
    </div>
  );
}
